/** @file Command input JSON Schema (the describe_command form) → the field specs CommandForm renders. */
import type { EntityKind } from '@pose2d/format/types';
import { REF_RULES } from './refRules.data';

/** What every field carries: where it sits in the input object, and its schema-level facts. */
export interface FieldBase {
  /** Last path segment: the property name, shown as the label (an API identifier, never translated). */
  key: string;
  /** Property path inside the command input, e.g. `['value', 'x']`. */
  path: readonly string[];
  required: boolean;
  /** The schema also accepts null (`anyOf [X, null]`). */
  nullable: boolean;
  /** The schema default (zod `.default()`), or undefined. */
  defaultValue: unknown;
}

/** One editable value of a command input; `json` is the fallback for shapes without a dedicated control. */
export type FieldSpec =
  | (FieldBase & { kind: 'number'; integer: boolean; min: number | null; max: number | null })
  | (FieldBase & { kind: 'text'; pattern: string | null })
  | (FieldBase & { kind: 'flag' })
  | (FieldBase & { kind: 'choice'; options: readonly (string | number)[] })
  | (FieldBase & { kind: 'color'; alpha: boolean })
  | (FieldBase & { kind: 'vector'; size: number })
  | (FieldBase & { kind: 'ref'; entity: EntityKind })
  | (FieldBase & { kind: 'group'; fields: readonly FieldSpec[] })
  | (FieldBase & { kind: 'json' });

type Schema = Readonly<Record<string, unknown>>;
type Spec<K extends FieldSpec['kind']> = Omit<Extract<FieldSpec, { kind: K }>, keyof FieldBase>;
type AnySpec = { [K in FieldSpec['kind']]: Spec<K> }[FieldSpec['kind']];

const COLOR: Readonly<Record<string, boolean>> = { '^#[0-9a-fA-F]{8}$': true, '^#[0-9a-fA-F]{6}$': false };
const asSchema = (v: unknown): Schema => (typeof v === 'object' && v !== null ? (v as Schema) : {});
const list = (v: unknown): Schema[] => (Array.isArray(v) ? v.map(asSchema) : []);
/** A bound below the safe-integer range (zod writes ±MAX_SAFE_INTEGER for a plain `.int()`), else null. */
const bound = (v: unknown): number | null => (typeof v === 'number' && Math.abs(v) < Number.MAX_SAFE_INTEGER ? v : null);

/** Bounds of a number schema; exclusive bounds show as plain bounds (the command still refuses the edge). */
function numberSpec(s: Schema): Spec<'number'> {
  const min = bound(s['minimum']) ?? bound(s['exclusiveMinimum']);
  const max = bound(s['maximum']) ?? bound(s['exclusiveMaximum']);
  return { kind: 'number', integer: s['type'] === 'integer', min, max };
}

/** A fixed-length tuple of plain numbers (`[x, y]`, ease handles), else null. */
function vectorSpec(s: Schema): Spec<'vector'> | null {
  const items = list(s['prefixItems']);
  const fixed = items.length > 0 && s['minItems'] === items.length && s['maxItems'] === items.length;
  return fixed && items.every((i) => i['type'] === 'number') ? { kind: 'vector', size: items.length } : null;
}

/** Strings: colours by their exact pattern, entity references by key (unpatterned names only), else text. */
function stringSpec(s: Schema, ref: EntityKind | null): AnySpec {
  const pattern = typeof s['pattern'] === 'string' ? s['pattern'] : null;
  const alpha = pattern === null ? undefined : COLOR[pattern];
  if (alpha !== undefined) return { kind: 'color', alpha };
  if (pattern === null && ref !== null) return { kind: 'ref', entity: ref };
  return { kind: 'text', pattern };
}

/** Every `anyOf`/`oneOf` member is a `const`: a choice among them; else null. */
function constChoice(s: Schema): Spec<'choice'> | null {
  const members = list(s['anyOf'] ?? s['oneOf']);
  if (members.length === 0 || !members.every((m) => 'const' in m)) return null;
  return { kind: 'choice', options: members.map((m) => m['const'] as string | number) };
}

/** The entity kind key `key` names at `path` in `command`, or null (an animation's `target` is free text). */
function refOf(command: string, key: string, path: readonly string[]): EntityKind | null {
  if (key !== 'target') return (REF_RULES.keys as Readonly<Record<string, EntityKind>>)[key] ?? null;
  const override = path.length === 1 ? (REF_RULES.commandTargets as Readonly<Record<string, EntityKind | undefined>>)[command] : undefined;
  if (override !== undefined) return override;
  const table: Readonly<Record<string, EntityKind>> = path.length === 1 ? REF_RULES.targets : REF_RULES.nestedTargets;
  return table[command.split('.')[0] ?? ''] ?? null;
}

/** A plain object (properties, no record keys) becomes a group of its fields; anything else is JSON. */
function objectSpec(command: string, path: readonly string[], s: Schema): AnySpec {
  const plain = s['type'] === 'object' && s['properties'] !== undefined && s['propertyNames'] === undefined;
  return plain ? { kind: 'group', fields: fieldsOf(command, s, path) } : { kind: 'json' };
}

/** Kind-specific part of one (non-null) schema node by its `type`; nested objects recurse through fieldsOf. */
function typedSpec(command: string, key: string, path: readonly string[], s: Schema): AnySpec {
  const type = s['type'];
  if (type === 'number' || type === 'integer') return numberSpec(s);
  if (type === 'boolean') return { kind: 'flag' };
  if (type === 'string') return stringSpec(s, refOf(command, key, path));
  if (type === 'array') return vectorSpec(s) ?? { kind: 'json' };
  return objectSpec(command, path, s);
}

/** Enums and const unions are choices; everything else goes by type. */
function specOf(command: string, key: string, path: readonly string[], s: Schema): AnySpec {
  if (Array.isArray(s['enum'])) return { kind: 'choice', options: s['enum'] as (string | number)[] };
  return constChoice(s) ?? typedSpec(command, key, path, s);
}

/** `anyOf [X, { type: 'null' }]` → X marked nullable; any other node unchanged. */
function unwrapNull(s: Schema): { node: Schema; nullable: boolean } {
  const members = list(s['anyOf']);
  const rest = members.filter((m) => m['type'] !== 'null');
  return members.length === 2 && rest.length === 1 ? { node: rest[0] ?? {}, nullable: true } : { node: s, nullable: false };
}

/** A union root (`anyOf`/`oneOf` of objects, no `properties`) as one object: keys of every member, required only if in all. */
function mergeUnion(s: Schema): Schema {
  const members = list(s['anyOf'] ?? s['oneOf']).filter((m) => m['properties'] !== undefined);
  if (s['properties'] !== undefined || members.length === 0) return s;
  const properties: Record<string, unknown> = {};
  for (const m of members) for (const [k, v] of Object.entries(asSchema(m['properties']))) properties[k] ??= v;
  const requiredIn = (m: Schema): string[] => (Array.isArray(m['required']) ? (m['required'] as string[]) : []);
  const required = Object.keys(properties).filter((k) => members.every((m) => requiredIn(m).includes(k)));
  return { properties, required };
}

/** Fields of an object schema in property order; `path` is the object's own path ([] for the input root). */
export function fieldsOf(command: string, input: Readonly<Record<string, unknown>>, path: readonly string[] = []): FieldSpec[] {
  const schema = mergeUnion(input);
  const required = new Set(Array.isArray(schema['required']) ? (schema['required'] as string[]) : []);
  return Object.entries(asSchema(schema['properties'])).map(([key, raw]): FieldSpec => {
    const { node, nullable } = unwrapNull(asSchema(raw));
    const base: FieldBase = { key, path: [...path, key], required: required.has(key), nullable, defaultValue: node['default'] ?? asSchema(raw)['default'] };
    return { ...base, ...specOf(command, key, base.path, node) };
  });
}
